import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Spinner,
  UncontrolledDropdown,
} from "reactstrap";
import { Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { apiListCases } from "../../helpers/backend_api";

const CaseManagement = () => {
  document.title = "Cases | ApprentAI";
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiListCases({ limit: 50, offset: 0 })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data || res;
        const list = payload?.case_list || payload?.case_list_data || [];
        setRows(Array.isArray(list) ? list : []);
        setTotal(payload?.total_case || payload?.customer_case_count || 0);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || "Failed to load cases");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const columns = useMemo(
    () => [
      {
        name: <span className="fw-bolder fs-14">Case ID</span>,
        selector: (row) => row.id || row.case_id,
        sortable: true,
      },
      {
        name: <span className="fw-bolder fs-14">Case Parties</span>,
        selector: (row) =>
          row.case_parties || row.parties || `${row.party_1 || ""} v. ${row.party_2 || ""}`,
        sortable: true,
      },
      {
        name: <span className="fw-bolder fs-14">Nickname</span>,
        selector: (row) => row.case_name || row.nickname || "—",
        sortable: true,
      },
      {
        name: <span className="fw-bolder fs-14">Lead Attorney</span>,
        selector: (row) => row.lead_attorney || "—",
        sortable: true,
      },
      {
        name: <span className="fw-bolder fs-14">Status</span>,
        selector: (row) => row.status_name || row.case_status?.status_name || "—",
        sortable: true,
      },
      {
        name: <span className="fw-bolder fs-14">Progression</span>,
        selector: (row) =>
          row.progression !== undefined ? `${row.progression}%` : "—",
        sortable: true,
      },
      {
        name: <span className="fw-bolder fs-14">Evidence Lab</span>,
        cell: () => <Button className="btn btn-secondary">Go</Button>,
      },
      {
        name: <span className="fw-bolder fs-14">Action</span>,
        cell: () => (
          <UncontrolledDropdown className="dropdown d-inline-block">
            <DropdownToggle className="btn btn-secondary btn-sm" tag="button">
              <i className="ri-more-fill align-middle"></i>
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu-end">
              <DropdownItem href="#!">
                <i className="ri-eye-fill align-bottom me-2 text-muted"></i>More Info
              </DropdownItem>
              <DropdownItem className="edit-item-btn">
                <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>Edit
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        ),
      },
    ],
    []
  );

  const customStyles = {
    cells: {
      style: {
        paddingLeft: "8px",
        paddingRight: "8px",
        border: "1px solid rgba(0,0,0,.04)",
        fontSize: "15px",
      },
    },
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="CASES" pageTitle="Dashboards" />
        <Link to="/addcases" className="my-3 btn btn-primary">
          Start a New Case
        </Link>
        <div className="text-muted small mb-2 mt-2">Total cases: {total}</div>

        {error ? <Alert color="danger">{String(error)}</Alert> : null}

        <Row>
          <Col>
            <Card>
              <CardBody>
                {loading ? (
                  <div className="text-center py-4">
                    <Spinner /> Loading cases…
                  </div>
                ) : (
                  <DataTable
                    columns={columns}
                    data={rows}
                    customStyles={customStyles}
                    noDataComponent="No cases found."
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CaseManagement;
